import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { AuthModalsProvider } from './contexts/AuthModalsContext';
import { PublicRoute } from './components/PublicRoute';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminProtectedRoute } from './components/AdminProtectedRoute';
import { SubscriptionProtectedRoute } from './components/SubscriptionProtectedRoute';
import { MainLayout } from './components/MainLayout';
import { AuthModals } from './components/AuthModals';
import { usePixelTracking } from './hooks/usePixelTracking';
import { ActivityIndicator, View, StyleSheet } from 'react-native';
import { NETFLIX_COLORS } from './theme/netflixTheme';

// Eager load critical screens (shown immediately)
import HomeScreen from './screens/home/HomeScreen';
import SearchScreen from './screens/search/SearchScreen';
import PlayerScreen from './screens/player/PlayerScreen';

// Lazy load non-critical screens (loaded on demand)
const SeriesDetailScreen = React.lazy(() => import('./screens/series/SeriesDetailScreen'));
const WatchlistScreen = React.lazy(() => import('./screens/watchlist/WatchlistScreen'));
const ProfileScreen = React.lazy(() => import('./screens/profile/ProfileScreen'));
const PlansScreen = React.lazy(() => import('./screens/plans/PlansScreen'));
const WatchHistoryScreen = React.lazy(() => import('./screens/watchhistory/WatchHistoryScreen'));
const ConsumptionScreen = React.lazy(() => import('./screens/consumption/ConsumptionScreen'));
const AdminScreen = React.lazy(() => import('./screens/admin/AdminScreen'));

// Billing screens (eager load since they're part of profile section)
import { BillingDashboard } from './screens/profile/BillingDashboard';
import { BillingScreen } from './screens/profile/BillingScreen';

// Wrapper component para BillingDashboard
function BillingDashboardPage() {
  const navigate = useNavigate();
  return <BillingDashboard onBack={() => navigate('/profile')} />;
}

// Wrapper component para BillingScreen
function BillingHistoryPage() {
  const navigate = useNavigate();
  return <BillingScreen onBack={() => navigate('/profile/billing')} />;
}

const loadingStyles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: NETFLIX_COLORS.background,
  },
});

// Loading fallback component
const LoadingFallback = () => (
  <View style={loadingStyles.container}>
    <ActivityIndicator size="large" color={NETFLIX_COLORS.primary} />
  </View>
);

// Error Boundary
class ErrorBoundary extends React.Component<any, any> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error('Error caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        // eslint-disable-next-line react-native/no-inline-styles
        <div style={{ padding: '20px', color: '#fff', backgroundColor: '#0f0a1a', minHeight: '100vh' }}>
          <h1>Oops! Algo deu errado.</h1>
          <p>{this.state.error?.toString()}</p>
          {/* eslint-disable-next-line react-native/no-inline-styles */}
          <button onClick={() => window.location.reload()} style={{ padding: '10px 20px', cursor: 'pointer' }}>
            Recarregar página
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
    },
  },
});

function AppRoutes() {
  usePixelTracking();

  return (
    <>
      <AuthModals />
      <Routes>

      {/* Public routes - viewable without login */}
      <Route
        path="/"
        element={
          <PublicRoute>
            <MainLayout>
              <HomeScreen />
            </MainLayout>
          </PublicRoute>
        }
      />
      <Route
        path="/search"
        element={
          <PublicRoute>
            <MainLayout>
              <SearchScreen />
            </MainLayout>
          </PublicRoute>
        }
      />
      <Route
        path="/series/:id"
        element={
          <PublicRoute>
            <MainLayout>
              <Suspense fallback={<LoadingFallback />}>
                <SeriesDetailScreen />
              </Suspense>
            </MainLayout>
          </PublicRoute>
        }
      />
      <Route
        path="/watch/:seriesId/:episodeNumber"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PlayerScreen />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/watchlist"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Suspense fallback={<LoadingFallback />}>
                <WatchlistScreen />
              </Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Suspense fallback={<LoadingFallback />}>
                <ProfileScreen />
              </Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/billing"
        element={
          <ProtectedRoute>
            <MainLayout>
              <BillingDashboardPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/billing/history"
        element={
          <ProtectedRoute>
            <MainLayout>
              <BillingHistoryPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/plans"
        element={
          <PublicRoute>
            <MainLayout>
              <Suspense fallback={<LoadingFallback />}>
                <PlansScreen />
              </Suspense>
            </MainLayout>
          </PublicRoute>
        }
      />
      <Route
        path="/watch-history"
        element={
          <SubscriptionProtectedRoute>
            <MainLayout>
              <Suspense fallback={<LoadingFallback />}>
                <WatchHistoryScreen />
              </Suspense>
            </MainLayout>
          </SubscriptionProtectedRoute>
        }
      />
      <Route
        path="/consumption"
        element={
          <SubscriptionProtectedRoute>
            <MainLayout>
              <Suspense fallback={<LoadingFallback />}>
                <ConsumptionScreen />
              </Suspense>
            </MainLayout>
          </SubscriptionProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <AdminScreen />
            </Suspense>
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/:tab"
        element={
          <AdminProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <AdminScreen />
            </Suspense>
          </AdminProtectedRoute>
        }
      />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AuthModalsProvider>
            <QueryClientProvider client={queryClient}>
              {/* eslint-disable-next-line react-native/no-inline-styles */}
              <div style={{ width: '100%', minHeight: '100vh', overflow: 'auto' }}>
                <AppRoutes />
              </div>
            </QueryClientProvider>
          </AuthModalsProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            zIndex: 99999999,
            backgroundColor: '#1a1a1a',
            color: '#fff',
            fontSize: '16px',
            padding: '16px 24px',
            borderRadius: '8px',
          },
          success: {
            style: {
              backgroundColor: '#10b981',
            },
          },
          error: {
            style: {
              backgroundColor: '#ef4444',
            },
          },
        }}
      />
    </ErrorBoundary>
  );
}
